import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import { styled } from '@mui/material/styles';
import api from '../../../Middleware/api/api';
import { useAuth } from '../../../services/contexts';
import LogoLoader from '../../../components/LogoLoader';
import LoadingButton from '../../../components/core/LoadingButton';

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialogContent-root': {
    padding: theme.spacing(2),
  },
  '& .MuiDialogActions-root': {
    padding: theme.spacing(1),
  },
}));

const OTHER = '__other__';

/** The backend's error codes, mapped to what the admin should read. */
const ERROR_CODE_KEYS = {
  UNATTRIBUTED_ENERGY_ALREADY_ALLOCATED: 'MSGAllocateAlreadyAllocated',
  CONTRACT_NOT_FOUND: 'MSGAllocateContractNotFound',
  OPERATOR_NOT_FOUND: 'MSGAllocateOperatorNotFound',
  DATA_NOT_FOUND: 'MSGAllocateGapNotFound',
  INVALID_INPUT_ERROR: 'MSGAllocateInvalidInput',
};

/**
 * OLISYS-5007: allocates an "Unknown Charging Session" - energy measured at a charge point with no session
 * to attribute it to - to the contract it belongs to, which bills it and completes the session.
 *
 * @param {PropTypes.InferProps<AllocateUnknownSessionDialog.propTypes>} props
 */
const AllocateUnknownSessionDialog = ({
  open,
  crId,
  handleClose,
  onAllocated,
}) => {
  const { t, i18n } = useTranslation();
  const { typography } = useTheme();
  const { token } = useAuth();

  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [position, setPosition] = useState(null);
  const [selected, setSelected] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [apiError, setApiError] = useState(null);
  // A manually entered Kunden-Nr is resolved to its contract before it can be chosen, so the admin sees who
  // they are about to bill rather than committing an irreversible allocation against a number they typed.
  const [resolvedContract, setResolvedContract] = useState(null);
  const [resolving, setResolving] = useState(false);
  const [resolveError, setResolveError] = useState(null);
  // OLISYS-5007 review: the customer is searched by name or Kunden-Nr, so an admin who knows who was
  // charging does not have to look their number up first.
  const [searchInput, setSearchInput] = useState('');
  const [searchOptions, setSearchOptions] = useState([]);
  // What allocating to the chosen contract would do, asked before the confirm step: which bill the
  // amount lands on, and anything the backend would refuse.
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  // A preview that never answered is not the same as one that answered "fine". Allocation is
  // irreversible and the preview is what says which invoice the amount lands on, so a failure blocks
  // the confirm step instead of quietly letting it through.
  const [previewFailed, setPreviewFailed] = useState(false);
  const [previewAttempt, setPreviewAttempt] = useState(0);

  useEffect(() => {
    if (!open || !crId) return undefined;
    // Only the request belonging to the dialog currently on screen may write state. Closing and reopening
    // on a different row leaves the first request in flight, and a late response would otherwise replace
    // this gap's details - including its contract list - while submit still sends the crId shown here.
    // Allocation is irreversible, so that mismatch would bill the wrong session.
    let active = true;
    setLoading(true);
    setLoadError(null);
    setPosition(null);
    setSelected(null);
    setConfirming(false);
    setApiError(null);
    setResolvedContract(null);
    setResolveError(null);
    setSearchInput('');
    setSearchOptions([]);
    setPreview(null);
    setPreviewFailed(false);
    api
      .fetchUnattributedEnergyPosition(token, crId)
      .then((response) => {
        if (!active) return;
        const data = response?.data?.data ?? null;
        setPosition(data);
        // Nothing to suggest is the normal case, not an error state: candidates come from sessions that
        // failed to attribute inside the gap window, and most gaps have none. Preselect the manual field
        // only when there is nothing at all to pick from.
        // OLISYS-5007 review: only the detector's own candidates are offered as a shortcut - contracts
        // merely registered for the charge point are not. There are dozens of them at a busy location,
        // being registered says nothing about who was charging, and picking from that list is guessing
        // dressed up as a choice. Everything else goes through the search.
        if ((data?.candidateContracts?.length ?? 0) === 0) setSelected(OTHER);
      })
      .catch(() => {
        if (active) setLoadError(t('MSGAllocateLoadFailed'));
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [open, crId]);

  // OLISYS-5007 review: searches while the admin types, debounced so a name is looked up once rather
  // than per keystroke. The backend scopes the result to this gap's operator.
  useEffect(() => {
    if (selected !== OTHER) return undefined;
    const term = searchInput.trim();
    if (term.length < 2) {
      setSearchOptions([]);
      return undefined;
    }
    let active = true;
    setResolving(true);
    setResolveError(null);
    const timer = setTimeout(() => {
      api
        .searchUnattributedEnergyContracts(token, crId, term)
        .then((response) => {
          if (active) setSearchOptions(response?.data?.data ?? []);
        })
        .catch(() => {
          if (active) setResolveError(t('LBLSomethingwentWrong'));
        })
        .finally(() => {
          if (active) setResolving(false);
        });
    }, 300);
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [searchInput, selected, crId]);

  const alreadyAllocated = position?.status === 'ATTRIBUTED';

  const allOptions = position?.candidateContracts ?? [];
  // Everything downstream reads the chosen *option*, never the raw text: a typed Kunden-Nr only becomes a
  // choice once the backend has resolved it to a contract, so there is no way to submit an unresolved number.
  const chosenOption =
    selected === OTHER
      ? resolvedContract
      : allOptions.find((option) => option.contractKey === selected);
  const chosenKey = chosenOption?.contractKey ?? null;
  const customerLabel = chosenOption
    ? [chosenOption.endCustomerId, chosenOption.customerFullName]
        .filter(Boolean)
        .join(' - ')
    : '';
  // The contract is not registered for this charge point. Allocation is still allowed - the admin may be
  // correcting a stale contract, and the backend does not reject it either - but they should see it.
  const notEntitled = Boolean(chosenKey) && !chosenOption?.entitled;

  // OLISYS-5007 review: ask what this allocation would do as soon as a contract is chosen. A gap is
  // usually found after its own billing period closed, in which case the amount lands on the next bill
  // instead - invisible from here unless it is said.
  useEffect(() => {
    if (!chosenKey || !open) {
      setPreview(null);
      setPreviewFailed(false);
      return undefined;
    }
    let active = true;
    setPreviewing(true);
    setPreviewFailed(false);
    api
      .previewUnattributedEnergyAllocation(token, crId, chosenKey)
      .then((response) => {
        if (active) setPreview(response?.data?.data ?? null);
      })
      .catch(() => {
        if (active) {
          setPreview(null);
          setPreviewFailed(true);
        }
      })
      .finally(() => {
        if (active) setPreviewing(false);
      });
    return () => {
      active = false;
    };
  }, [chosenKey, crId, open, previewAttempt]);

  const refusedByBackend = Boolean(preview) && preview.allowed === false;
  // Ready only once the backend has actually answered for this contract.
  const previewReady = Boolean(preview) && preview.allowed !== false;

  const energyLabel = () => {
    if (position?.amountEnergy === null || position?.amountEnergy === undefined)
      return '';
    // Formatted for the active locale rather than by swapping separators in a de-DE string: that turns
    // 1.234,56 into 1.234.56, and a gap whose register sat frozen for weeks easily exceeds 999 kWh - the
    // confirm step names this number as the amount about to be billed.
    return new Intl.NumberFormat(i18n.language === 'en' ? 'en-GB' : 'de-DE', {
      style: 'decimal',
      minimumFractionDigits: 2,
      maximumFractionDigits: 4,
    }).format(position.amountEnergy);
  };

  const periodLabel = () => {
    if (!position?.startDate) return '';
    const start = new Date(position.startDate).toLocaleString(
      i18n.language === 'en' ? 'en-GB' : 'de-DE'
    );
    const end = position.endDate
      ? new Date(position.endDate).toLocaleString(
          i18n.language === 'en' ? 'en-GB' : 'de-DE'
        )
      : '';
    return `${start} - ${end}`;
  };

  // Led by the Kunden-Nr, because that is the identifier operators recognise and the one the list column
  // beside this dialog shows. The contract number still travels along - it is what gets billed.
  const optionLabel = (option) => {
    const customer = option.endCustomerId || option.contractKey;
    const name = option.customerFullName ? ` - ${option.customerFullName}` : '';
    const tariff =
      option.fixTariff !== null && option.fixTariff !== undefined
        ? ` · ${option.fixTariff} / ${option.varTariff}`
        : '';
    return `${customer}${name} (${option.contractKey}${tariff})`;
  };

  const renderOptions = (options, label) =>
    options.length > 0 && (
      <>
        <Typography variant="subtitle2" align="left" sx={{ mt: 3 }}>
          {label}
        </Typography>
        {options.map((option) => (
          <FormControlLabel
            key={option.contractKey}
            value={option.contractKey}
            control={<Radio />}
            label={optionLabel(option)}
          />
        ))}
      </>
    );

  const handleSubmit = () => {
    if (!chosenKey) return;
    setSubmitting(true);
    setApiError(null);
    api
      .allocateUnattributedEnergy(token, crId, chosenKey)
      .then(() => onAllocated(chosenKey))
      .catch((error) => {
        const code = error?.response?.data?.error?.errorCodeResponse;
        setApiError(t(ERROR_CODE_KEYS[code] ?? 'LBLSomethingwentWrong'));
        setConfirming(false);
        // A 409 means our row is stale - someone else allocated it, or an attempt is in flight. Refresh the
        // list even though the call failed, so the admin is not looking at a session that no longer exists.
        // Keyed on the HTTP status as well as the application code: a 409 that arrives without that payload
        // is still a stale row, and leaving it on screen invites a second attempt at a settled session.
        if (
          error?.response?.status === 409 ||
          code === 'UNATTRIBUTED_ENERGY_ALREADY_ALLOCATED'
        ) {
          onAllocated(null);
        }
      })
      .finally(() => setSubmitting(false));
  };

  const body = () => {
    if (loading) return null;
    if (loadError) return <Alert severity="error">{loadError}</Alert>;
    if (!position) return null;

    if (alreadyAllocated) {
      return (
        <Alert severity="info">
          {t('LBLAllocateAlreadyAllocated', {
            contractKey: position.contractKey,
          })}
        </Alert>
      );
    }

    if (confirming) {
      return (
        <Stack spacing={4}>
          <Typography>
            {t('MSGAllocateConfirm', {
              energy: energyLabel(),
              customer: customerLabel || chosenKey,
              contractKey: chosenKey,
            })}
          </Typography>
          {notEntitled && (
            <Alert severity="warning">
              {t('LBLAllocateNotEntitledWarning')}
            </Alert>
          )}
          {/* The billing destination belongs here too: this is the step the admin commits from. */}
          {preview?.billedInLaterPeriod && (
            <Alert severity="info">{t('LBLAllocateBilledNextInvoice')}</Alert>
          )}
          {apiError && <Alert severity="error">{apiError}</Alert>}
        </Stack>
      );
    }

    const candidates = position.candidateContracts ?? [];

    return (
      <Stack spacing={2} alignItems="stretch">
        <Typography align="left">
          {t('LBLAllocateUnknownSessionIntro')}
        </Typography>

        <Stack spacing={1} sx={{ my: 3 }}>
          <Typography align="left">
            <strong>{t('LBLAllocateConnector')}:</strong> {position.connectorId}
          </Typography>
          <Typography align="left">
            <strong>{t('LBLAllocatePeriod')}:</strong> {periodLabel()}
          </Typography>
          <Typography align="left">
            <strong>{t('LBLAllocateEnergy')}:</strong> {energyLabel()} kWh
          </Typography>
        </Stack>

        {position.startDateEstimated && (
          <Alert severity="warning">{t('LBLAllocateEstimatedStart')}</Alert>
        )}

        {candidates.length === 0 && (
          <Alert severity="info">{t('LBLAllocateNoCandidates')}</Alert>
        )}

        <Divider />

        {/*
          The radios exist only to choose between the detector's suggestions and the search. With no
          suggestions - the normal case - there is nothing to choose, so the search stands alone rather
          than behind a single radio button.
        */}
        {candidates.length > 0 && (
          <FormControl>
            <RadioGroup
              value={selected ?? ''}
              onChange={(event) => setSelected(event.target.value)}
            >
              {renderOptions(candidates, t('LBLAllocateCandidates'))}
              <FormControlLabel
                value={OTHER}
                control={<Radio />}
                label={t('LBLAllocateOtherContract')}
              />
            </RadioGroup>
          </FormControl>
        )}

        {selected === OTHER && (
          <Stack spacing={2}>
            {/*
              A dropdown over this operator's customers, searched by name or Kundennummer - the same two
              identifiers the Nutzer page filters by. Typing an exact customer number was the previous
              way in, and it assumed the admin already knew it; someone reading a gap usually knows a
              name instead. Options carry the contract behind them, so choosing one is choosing a
              contract, never a string that still has to resolve.
            */}
            <Autocomplete
              id="allocateCustomerSearch"
              options={searchOptions}
              loading={resolving}
              value={resolvedContract}
              onChange={(event, option) => setResolvedContract(option)}
              inputValue={searchInput}
              onInputChange={(event, value, reason) => {
                setSearchInput(value);
                // Typing after a pick, or clearing the field, must drop that pick - otherwise the
                // dialog shows one query while chosenKey still points at the contract chosen before,
                // and Allocate would bill that one. "reset" is excluded: MUI fires it right after a
                // selection to sync the input, and it would immediately undo the choice.
                if (reason === 'input' || reason === 'clear') {
                  setResolvedContract(null);
                }
              }}
              getOptionLabel={(option) => optionLabel(option)}
              isOptionEqualToValue={(option, value) =>
                option.contractKey === value?.contractKey
              }
              filterOptions={(options) => options}
              noOptionsText={
                searchInput.trim().length < 2
                  ? t('MSGAllocateSearchCustomerHint')
                  : t('MSGAllocateCustomerNotFound')
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  margin="dense"
                  label={t('LBLAllocateSearchCustomer')}
                  variant="outlined"
                  fullWidth
                />
              )}
            />
            {resolveError && <Alert severity="error">{resolveError}</Alert>}
          </Stack>
        )}

        {notEntitled && !confirming && (
          <Alert severity="warning">{t('LBLAllocateNotEntitledWarning')}</Alert>
        )}
        {/*
          Which bill this lands on. Gaps are found late by their nature, so "the period is already
          billed, this goes on the next invoice" is the ordinary case rather than an exception - and it
          is not visible anywhere else in the flow.
        */}
        {!refusedByBackend && preview?.billedInLaterPeriod && (
          <Alert severity="info">{t('LBLAllocateBilledNextInvoice')}</Alert>
        )}
        {refusedByBackend && (
          <Alert severity="error">
            {preview.refusalReason || t('MSGAllocateInvalidInput')}
          </Alert>
        )}
        {previewFailed && (
          <Alert
            severity="warning"
            action={
              <Button
                color="inherit"
                size="small"
                onClick={() => setPreviewAttempt((attempt) => attempt + 1)}
              >
                {t('BTNAllocateRetryPreview')}
              </Button>
            }
          >
            {t('MSGAllocatePreviewFailed')}
          </Alert>
        )}
        {apiError && <Alert severity="error">{apiError}</Alert>}
      </Stack>
    );
  };

  const actions = () => {
    if (loading) return null;
    if (loadError || alreadyAllocated) {
      return (
        <Button
          variant="contained"
          color="inherit"
          sx={{ px: 12 }}
          onClick={handleClose}
        >
          {t('close')}
        </Button>
      );
    }
    if (confirming) {
      return (
        <>
          <Button
            variant="text"
            onClick={() => setConfirming(false)}
            disabled={submitting}
          >
            {t('cancel')}
          </Button>
          <LoadingButton
            variant="contained"
            color="primary"
            sx={{ px: 8 }}
            loading={submitting}
            disabled={
              submitting || !chosenKey || refusedByBackend || !previewReady
            }
            onClick={handleSubmit}
          >
            {t('BTNAllocateConfirm')}
          </LoadingButton>
        </>
      );
    }
    return (
      <>
        <Button variant="text" onClick={handleClose}>
          {t('cancel')}
        </Button>
        <Button
          variant="contained"
          sx={{ px: 8 }}
          // Held while the preview is in flight, and blocked when it came back refused - the backend
          // would reject this anyway, and an error after the confirm step reads like a failure rather
          // than a choice that was never available.
          disabled={
            !chosenKey || previewing || refusedByBackend || !previewReady
          }
          onClick={() => setConfirming(true)}
        >
          {t('BTNAllocateUnknownSession')}
        </Button>
      </>
    );
  };

  return (
    <BootstrapDialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <LogoLoader loading={loading} />
      <DialogTitle
        align="center"
        fontWeight={700}
        fontSize={typography.h5.fontSize}
        mt={6}
      >
        {confirming
          ? t('LBLAllocateConfirmTitle')
          : t('LBLAllocateUnknownSessionTitle')}
      </DialogTitle>
      <IconButton
        aria-label="close"
        onClick={handleClose}
        sx={(theme) => ({
          position: 'absolute',
          right: 8,
          top: 8,
          color: theme.palette.grey[500],
        })}
      >
        <CloseIcon />
      </IconButton>
      <DialogContent sx={{ mx: 3, mt: 2 }}>{body()}</DialogContent>
      <DialogActions sx={{ mb: 6, justifyContent: 'center' }}>
        {actions()}
      </DialogActions>
    </BootstrapDialog>
  );
};

AllocateUnknownSessionDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  crId: PropTypes.string,
  handleClose: PropTypes.func.isRequired,
  /** Called with the allocated contract key on success, or null when the list merely needs refreshing. */
  onAllocated: PropTypes.func.isRequired,
};

export default AllocateUnknownSessionDialog;
